import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { reqEditText, reqGetJob, reqGetStreamingData, reqStopGenerating } from 'api/requests/main';
import { getAdditionalSettings, getContextQuery } from 'api/queries/job';
import { getDefaultFilters, normalizeFilters } from 'helpers/filters';
import { AbortControllers, GeneratedJobType, JobOrderRegenerateBody } from './types';
import { SelectedLanguageType, Streaming } from 'store/global/types';
import ProductTourTooltips from 'pages/productTour/tooltips';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { logEventFirebase } from 'utils/config/firebase';
import { getSignatureQuery } from 'api/queries/settings';
import RightSidebar from 'components/RightSidebar';
import { showErrorMessages } from 'helpers/error';
import { JobParamsType } from 'store/auth/types';
import Sidebar from '../../components/Sidebar';
import { Unsubscribe } from 'firebase/auth';
import IntakeNotes from './IntakeNotes';
import Box from '@mui/material/Box';
import 'animate.css';
import {
    addGroupToConversation,
    clearStreaming,
    setConversationGroups,
    setShowTour,
    setStreamingValues,
    updateConversationGroup,
} from 'store/global/globalSlice';
import { debounce } from '@mui/material';
import { Group } from './types';
import { getInstructionQuery } from 'api/queries/user';
import { useSectionsFeatureFlag } from 'hooks/useSectionsFeatureFlag';
import {
    createRegenerateRequestBody,
    getCountryCode,
    getROneFlag,
    parseParameters,
} from './jobOrderUtils';
import { createGroupsFromMessages, findMessageAncestor, getGroupColor } from './groupUtils';
import { JobAds } from './JobAds';
import { HiddenJobAds } from './HiddenJobAds';
import { JOB_GID_QUERY_PARAM_NAME } from 'utils/config/constants';
import { biasCheckFail } from '../../helpers/messages';
import { AxiosError } from 'axios';

const JobGenerator: FC = () => {
    const { userSettings, userDefaultFilters, defaultFilters } = useAppSelector(
        (state) => state.auth,
    );
    const {
        streamingValues,
        additionalSettings,
        targetCountries,
        conversationGroups,
        jobOrderData,
    } = useAppSelector((state) => state.global);
    const dispatch = useAppDispatch();

    const [isGeneratingJob, setIsGeneratingJob] = useState<boolean>(false);
    const [showSkeleton, setShowSkeleton] = useState<string[]>([]);
    const [generatingGroupId, setGeneratingGroupId] = useState<string | undefined>(undefined);
    const [item, setItem] = useState<GeneratedJobType | null>(null);
    const [isScrolling, setIsScrolling] = useState<boolean>(false);
    const [outputWidth, setOutputWidth] = useState<number>(0);
    const [paddingLeft, setPaddingLeft] = useState<string>('');

    const existingMessages = useRef<string[] | null>(null);
    const selectedDocumentId = useRef<string>('');
    const closeStreamingController = useRef<AbortControllers>({});
    const generateJobRef = useRef<AbortControllers>({});
    const jobContainerRef = useRef<HTMLDivElement>(null);

    const [searchParams] = useSearchParams();
    const { document_id } = useParams();
    const { isSectionsModeEnabled } = useSectionsFeatureFlag();
    const jobOrderId = searchParams.get(JOB_GID_QUERY_PARAM_NAME);

    const firstVersion = {
        language: item?.messages?.[0]?.language ?? '',
        targetCountry: item?.messages?.[0]?.country ?? '',
        brand: item?.messages?.[0]?.brand ?? '',
    };

    useEffect(() => {
        if (item) {
            const groupsArray = createGroupsFromMessages(item).reverse();
            dispatch(setConversationGroups(groupsArray));
        }
    }, [dispatch, item]);

    useEffect(() => {
        if (selectedDocumentId.current !== (document_id ?? '')) {
            setShowSkeleton([]);
            setGeneratingGroupId(undefined);
        }

        let unsubscribe: Unsubscribe | null = null;
        if (document_id) {
            unsubscribe = getContextQuery(document_id, setItem);
        }
        const unsubscribeInstructionQuery = getInstructionQuery(dispatch, document_id);

        return () => {
            if (unsubscribe) {
                unsubscribe();
                setItem(null);
                existingMessages.current = null;
            }
            if (unsubscribeInstructionQuery) {
                unsubscribeInstructionQuery();
            }
        };
    }, [dispatch, document_id]);

    useEffect(() => {
        if (item && item?.messages?.length !== 0 && item?.messages?.[0]?.content) {
            dispatch(setShowTour(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(item?.messages)]);

    useEffect(() => {
        if (item && item?.messages?.length !== 0 && existingMessages.current) {
            const withoutExistingOnes = item?.messages.filter(
                (item) => !existingMessages.current?.includes(item.message_id),
            );
            withoutExistingOnes?.forEach((message) => {
                dispatch(
                    setStreamingValues({
                        jobId: message.message_id,
                        value: '',
                        done: true,
                    }),
                );
                const ancestor = findMessageAncestor(message, item.messages);
                const group = conversationGroups.find(
                    (group: Group) => group.id === ancestor?.version.major,
                );
                if (ancestor && group) {
                    const updatedGroup = {
                        ...group,
                        messagesIds: [...group.messagesIds, message.message_id],
                    };
                    dispatch(updateConversationGroup(updatedGroup));
                }
                if (!ancestor) {
                    dispatch(
                        addGroupToConversation({
                            id: message.version.major,
                            color: getGroupColor(conversationGroups.length),
                            messagesIds: [message.message_id],
                            isCollapsed: false,
                        }),
                    );
                }

                setTimeout(() => {
                    setShowSkeleton((prev) => prev.filter((item) => item !== message.language));
                }, 0);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.messages?.length]);

    useEffect(() => {
        if (generatingGroupId) {
            scrollToGroup(generatingGroupId, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generatingGroupId]);

    useEffect(() => {
        if (showSkeleton.length > 0 && !generatingGroupId) {
            scrollToLeft();
        }
    }, [generatingGroupId, showSkeleton]);

    useEffect(() => {
        const checkIsProgressStreaming = (streamingValues: Streaming) => {
            for (const key in streamingValues) {
                if (!streamingValues[key].done) {
                    return true;
                }
            }
            return false;
        };
        const inProgress = checkIsProgressStreaming(streamingValues);
        if (!inProgress && showSkeleton?.length === 0) {
            setIsGeneratingJob(false);
        }
    }, [streamingValues, showSkeleton.length]);

    useEffect(() => {
        if (!document_id) {
            setPaddingLeft('170px');
        }
        getAdditionalSettings(dispatch);
        getSignatureQuery(dispatch);
        function getSize() {
            if (window.innerWidth > 1300) {
                setOutputWidth(590);
            } else {
                setOutputWidth(420);
            }
            if (window.innerHeight <= 750 && document_id) {
                setPaddingLeft('370px');
            }
            if (window.innerHeight > 750 && document_id) {
                setPaddingLeft('410px');
            }
        }
        getSize();
        window.addEventListener('resize', getSize);
        return () => window.removeEventListener('resize', getSize);
    }, [dispatch, document_id]);

    const getFilters = () => {
        if (userDefaultFilters) {
            return normalizeFilters(userDefaultFilters, additionalSettings);
        }
        return getDefaultFilters(userSettings, defaultFilters);
    };

    const clearStreamingAfterGenerating = (jobUniqKey: string) => {
        dispatch(clearStreaming({ jobId: jobUniqKey }));
    };

    const regenerate = async (
        languages: SelectedLanguageType[],
        prev_message_id: string | null | undefined,
        params?: JobParamsType,
        brand?: string,
        groupId?: string,
    ) => {
        try {
            selectedDocumentId.current = document_id as string;
            existingMessages.current = item?.messages.map((item) => item.message_id) ?? [];
            const lastMessage = item?.messages.find((item) => item.message_id === prev_message_id);
            setIsGeneratingJob(true);
            if (groupId) {
                const group = conversationGroups.find((g) => g.id === groupId);
                dispatch(
                    updateConversationGroup({ ...group, isCollapsed: false, isGenerating: true }),
                );
            }
            setGeneratingGroupId(groupId);
            for (const language of languages) {
                const countryCode = getCountryCode(targetCountries, language.country as string);
                const parameters = parseParameters(
                    params,
                    getFilters,
                    additionalSettings,
                    countryCode,
                );
                const rOneFlag = getROneFlag(lastMessage, isSectionsModeEnabled, jobOrderData);
                const requestBody: JobOrderRegenerateBody = createRegenerateRequestBody(
                    document_id,
                    prev_message_id,
                    language,
                    jobOrderData,
                    brand,
                    item?.intake_notes || '',
                    parameters,
                    !!item?.messages?.[0]?.use_signature,
                    rOneFlag,
                );

                const response = await reqGetJob<typeof requestBody>(requestBody);
                const { message_id, content } = await response.data;
                setIsGeneratingJob(true);
                dispatch(
                    setStreamingValues({
                        jobId: message_id,
                        value: content,
                        done: true,
                    }),
                );
            }
            setGeneratingGroupId(undefined);
            logEventFirebase('click_regenerate_job');
            return;
        } catch (error) {
            showErrorMessages(error, 'Regenerate');
            setIsGeneratingJob(false);
            console.log(error);
            return;
        }
    };

    const getStreaming = async ({
        stream_url,
        callback: _callback,
        jobUniqKey,
    }: {
        stream_url: string;
        callback?: (jobUniqKey: string) => void;
        jobUniqKey: string;
    }) => {
        try {
            const abortController = new AbortController();
            closeStreamingController.current = {
                ...closeStreamingController.current,
                [jobUniqKey]: abortController,
            };

            const response = await reqGetStreamingData(stream_url, abortController.signal);
            if (!response.ok) {
                throw new Error('Failed to fetch streaming data');
            }

            const content = await response.text();
            dispatch(
                setStreamingValues({
                    jobId: jobUniqKey,
                    value: content,
                    done: true,
                }),
            );
        } catch (error) {
            showErrorMessages(error, 'getStreaming');
            dispatch(
                setStreamingValues({
                    jobId: jobUniqKey,
                    value: '',
                    done: true,
                }),
            );
            console.log(error);
        }
    };

    const scrollToLeft = () => {
        jobContainerRef?.current?.scrollTo({
            left: 0,
            behavior: 'smooth',
        });
    };

    const scrollToGroup = (groupId: string, scrollToLastGroupItem: boolean) => {
        const marginBetweenGroups = 50;
        const marginBetweenGroupOutputs = 24;
        const collapsedMessageWidth = 10;
        let numberOfMessagesBehind = 0;
        let numberOfGroupsBehind = 0;
        let numberOfGroupMessagesBehind = 0;
        let numberOfCollapsedDisplayedMessages = 0;

        const group = conversationGroups.find((g) => g.id === groupId);

        if (item && group) {
            const groupIndex = conversationGroups.indexOf(group);
            const groupsBehind = conversationGroups.filter(
                (g, index) =>
                    (scrollToLastGroupItem ? index <= groupIndex : index < groupIndex) &&
                    !g.messagesIds.every((value) => item.hiddenMessages?.includes(value)),
            );

            groupsBehind.forEach((g) => {
                if (g.isCollapsed) {
                    numberOfCollapsedDisplayedMessages += g.messagesIds.length > 2 ? 2 : 1;
                    numberOfMessagesBehind++;
                } else {
                    numberOfMessagesBehind += g.messagesIds.filter(
                        (id) => !item?.hiddenMessages?.includes(id),
                    ).length;
                }
                g.messagesIds.forEach((mId) => {
                    if (item.hiddenMessages?.includes(mId)) {
                        numberOfGroupMessagesBehind--;
                        if (scrollToLastGroupItem) {
                            numberOfCollapsedDisplayedMessages--;
                        }
                    }
                });
            });
            numberOfGroupsBehind += groupsBehind.length;
            numberOfGroupMessagesBehind += groupsBehind
                .map((g) => (g.isCollapsed ? 0 : g.messagesIds.length - 1))
                .reduce((a, b) => a + b, 0);
        }

        const scrollDistance =
            numberOfMessagesBehind * outputWidth +
            numberOfGroupsBehind * marginBetweenGroups +
            numberOfGroupMessagesBehind * marginBetweenGroupOutputs +
            numberOfCollapsedDisplayedMessages * collapsedMessageWidth;

        jobContainerRef?.current?.scrollTo({
            left: scrollDistance,
            behavior: 'smooth',
        });
    };

    const stopGenerating = async (jobUniqKey: string) => {
        try {
            logEventFirebase('click_stop_generating');

            if (closeStreamingController.current[jobUniqKey]) {
                closeStreamingController.current[jobUniqKey]?.abort();
                delete closeStreamingController.current[jobUniqKey];
            }

            dispatch(
                setStreamingValues({
                    jobId: jobUniqKey,
                    value: '',
                    done: true,
                }),
            );

            await reqStopGenerating(document_id as string, jobUniqKey);
        } catch (err) {
            console.log(err);
        }
    };

    const handleTransitionEnd = (groupId: string, event: React.TransitionEvent) => {
        if (
            isGeneratingJob &&
            event.propertyName === 'margin-left' &&
            generatingGroupId === groupId
        ) {
            scrollToGroup(groupId, true);
        }
    };

    const handleScroll = debounce(() => {
        if (jobContainerRef.current && jobContainerRef.current.scrollLeft > 90) {
            setIsScrolling(true);
        } else {
            setIsScrolling(false);
        }
    });

    const handleRewrite = async (
        data: { markedHtml: string; prompt: string },
        messageId: string,
        groupId?: string,
    ) => {
        if (!document_id) {
            return;
        }

        try {
            setIsGeneratingJob(true);
            if (groupId) {
                const group = conversationGroups.find((g) => g.id === groupId);
                dispatch(
                    updateConversationGroup({ ...group, isCollapsed: false, isGenerating: true }),
                );
            }
            setGeneratingGroupId(groupId);
            const response = await reqEditText({
                conversation_id: document_id,
                message_id: messageId,
                prompt: data.prompt,
                marked_html: data.markedHtml.replace(/\(/g, '«').replace(/\)/g, '»'),
            });
            const { message_id, content } = await response.data;
            setIsGeneratingJob(true);
            dispatch(
                setStreamingValues({
                    jobId: message_id,
                    value: content,
                    done: true,
                }),
            );
            setGeneratingGroupId(undefined);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 422) {
                const detail = error.response.data?.detail;
                if (typeof detail === 'object' && detail?.error === 'bias_detected') {
                    biasCheckFail();
                    setIsGeneratingJob(false);
                    setGeneratingGroupId(undefined);
                    return;
                }
            }
            showErrorMessages(error, 'handleRewrite');
            setIsGeneratingJob(false);
            setGeneratingGroupId(undefined);
        }
    };

    return (
        <>
            {userSettings && <ProductTourTooltips userSettings={userSettings} />}
            <Sidebar />
            <RightSidebar>
                <Box className="flex justify-center left-container">
                    <Box
                        className={`job-generator ${isScrolling ? 'scrolling' : ''}`}
                        ref={jobContainerRef}
                        sx={{ paddingLeft: paddingLeft }}
                        onScroll={handleScroll}
                    >
                        <IntakeNotes
                            firstVersion={firstVersion}
                            defaultValue={item?.intake_notes}
                            isGeneratingJob={isGeneratingJob}
                            generateJobRef={generateJobRef}
                            selectedDocumentId={selectedDocumentId}
                            existingMessages={existingMessages}
                            clearStreamingAfterGenerating={clearStreamingAfterGenerating}
                            getStreaming={getStreaming}
                            setIsGeneratingJob={setIsGeneratingJob}
                            setShowSkeleton={setShowSkeleton}
                            getFilters={getFilters}
                            jobOrderId={item?.job_order_id ?? jobOrderId}
                            attachedFile={{
                                url: item?.messages?.[0]?.attached_file_download_url,
                                filename: item?.messages?.[0]?.attached_file_filename,
                            }}
                        />
                        <JobAds
                            item={item}
                            isGeneratingJob={isGeneratingJob}
                            regenerate={regenerate}
                            stopGenerating={stopGenerating}
                            scrollToGroup={scrollToGroup}
                            handleTransitionEnd={handleTransitionEnd}
                            handleRewriteFragment={handleRewrite}
                        />
                    </Box>
                </Box>
                <HiddenJobAds
                    item={item}
                    isGeneratingJob={isGeneratingJob}
                    regenerate={regenerate}
                />
            </RightSidebar>
        </>
    );
};

export default JobGenerator;
